import React, { useState } from 'react';
import { useTheme } from '../App';
import { Clock, DollarSign } from 'lucide-react';
import ReviewModal from './ReviewModal';
export type ActionSize = 'S' | 'M' | 'L';
export interface ActionProps {
  title: string;
  description: string;
  summary?: string;
  confidenceScore: number;
  valueType: 'Cash Unlocked' | 'GMV Uplift' | 'Risks Mitigated' | string;
  valueAmount: number;
  timeSaved: number;
  size: ActionSize;
  urgency?: 'Urgent' | 'High' | 'Medium' | 'Low';
  actionData?: any;
}
const getCredits = (size: ActionSize) => {
  switch (size) {
    case 'S':
      return 3;
    case 'M':
      return 6;
    case 'L':
      return 10;
    default:
      return 3;
  }
};
const getUrgencyColor = (urgency?: string) => {
  switch (urgency) {
    case 'Urgent':
      return 'bg-[#f44336]';
    case 'High':
      return 'bg-[#ff9800]';
    case 'Medium':
      return 'bg-[#ffc107]';
    case 'Low':
      return 'bg-[#4caf50]';
    default:
      return '';
  }
};
const ActionCard: React.FC<ActionProps> = ({
  title,
  description,
  summary,
  confidenceScore,
  valueType,
  valueAmount,
  timeSaved,
  size,
  urgency,
  actionData
}) => {
  const {
    isDarkMode
  } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const credits = getCredits(size);
  const urgencyColor = getUrgencyColor(urgency);
  const formatValue = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  return <>
      <div className={`
          relative rounded-lg overflow-hidden mb-4 border-l-4 border-[#2196f3]
          ${isDarkMode ? 'bg-[#0d1117]' : 'bg-white'} 
        `} data-id="element-49">
        <div className="p-4" data-id="element-50">
          <div className="flex justify-between items-start" data-id="element-51">
            <h3 className="text-lg font-bold" data-id="element-52">{title}</h3>
            {urgency && <div className={`${urgencyColor} text-white text-xs font-bold px-2 py-1 rounded`} data-id="element-53">
                {urgency}
              </div>}
          </div>
          <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-54">
            {description}
          </p>
          <div className="mt-3 flex justify-between items-center" data-id="element-55">
            <div className="flex items-center gap-4" data-id="element-56">
              <div className="flex items-center" data-id="element-57">
                <span className="font-bold text-[#2196f3]" data-id="element-58">
                  {confidenceScore}%
                </span>
                <span className="ml-1 text-sm text-gray-400" data-id="element-59">Confidence</span>
              </div>
              <div className="flex items-center" data-id="element-60">
                <DollarSign size={16} className="text-green-500" data-id="element-61" />
                <span className="font-bold" data-id="element-62">{formatValue(valueAmount)}</span>
                <span className="ml-1 text-sm text-gray-400" data-id="element-63">{valueType}</span>
              </div>
              <div className="flex items-center" data-id="element-64">
                <Clock size={16} className="text-purple-500 mr-1" data-id="element-65" />
                <span data-id="element-66">{timeSaved} mins</span>
              </div>
              <div className="text-sm text-gray-400" data-id="element-67">
                {size} ({credits} credits)
              </div>
            </div>
            <button className="px-4 py-2 bg-[#2196f3] text-white rounded-md hover:bg-[#1976d2] transition-colors" onClick={() => setIsModalOpen(true)} data-id="element-68">
              Review & Run
            </button>
          </div>
        </div>
      </div>
      {isModalOpen && <ReviewModal action={{
      title,
      description,
      confidenceScore,
      valueType,
      valueAmount,
      timeSaved,
      size,
      urgency,
      actionData
    }} onClose={() => setIsModalOpen(false)} data-id="element-69" />}
    </>;
};
export default ActionCard;